/* eslint-disable import/prefer-default-export */
import BaseFeature from '@appblocks/codegeneration/BaseFeature';
import ProjectConfiguration from '@appblocks/codegeneration/ProjectConfiguration';
import {
    CodeBlock,
    BlockType,
    SettingType,
    Define,
    ValidationMessageType,
    ValidationMessage,
    SettingState,
    Setting,
    TibboDataType,
    TimeFormats,
} from '@appblocks/codegeneration/types';


export const getCodeBlocks = (feature: BaseFeature): CodeBlock[] => {
    let blocks: CodeBlock[] = [];
    const generalFeature = feature.project.getFeature('general');
    let timeFormat = generalFeature.getSettingValue('TIME_FORMAT');

    if (timeFormat === TimeFormats.twelveHour) {
        timeFormat = 'hh:mm tt';
    } else {
        timeFormat = 'HH:mm';
    }

    const SCHEDULES = feature.getSettingValue('SCHEDULES');
    if (SCHEDULES.length === 0) {
        return blocks;
    }

    blocks.push({
        location: 'FUNCTION_DEFINITIONS',
        blockType: BlockType.CODE,
        template: `
function parseCron(cronExpr) {
  const fields = cronExpr.trim().split(/\\s+/);
  if (fields.length !== 6) {
    console.log('Cron must have 6 fields: minute hour day month dayOfWeek astro');
    return null;
  }

  const [minute, hour, dayOfMonth, month, dayOfWeek, astro] = fields;

  // The first 5 fields are similar to before.
  function parseField(field, min, max, allowList = false) {
    if (field === '*') return null; // wildcard

    if (allowList && field.includes(',')) {
      // e.g. "1,3,5" => [1,3,5]
      const arr = field.split(',').map(valStr => {
        const val = parseInt(valStr, 10);
        if (isNaN(val) || val < min || val > max) {
          console.log(\`Invalid value "\${valStr}" in dayOfWeek list "\${field}"\`);
          return null;
        }
        return val;
      });
      return arr;
    }

    // single integer
    const val = parseInt(field, 10);
    if (isNaN(val) || val < min || val > max) {
      console.log(\`Invalid field "\${field}", must be \${min}..\${max} or '*'\`);
      return null;
    }
    return val;
  }

  const parsedMinute = parseField(minute, 0, 59);
  const parsedHour = parseField(hour, 0, 23);
  const parsedDayOfMonth = parseField(dayOfMonth, 1, 31);
  const parsedMonth = parseField(month, 1, 12);
  const parsedDayOfWeek = astro !== '*' && (parsedDayOfMonth || parsedMonth) ? null : parseField(dayOfWeek, 0, 6, true);

  // The 6th field: astro => '*', 'r', 's', or 'rs'
  const validAstro = ['*', 'r', 's', 'rs'];
  if (!validAstro.includes(astro)) {
    console.log(\`astro field must be '*', 'r', 's', or 'rs', got "\${astro}"\`);
    return null;
  }

  return {
    minute: parsedMinute,
    hour: parsedHour,
    dayOfMonth: parsedDayOfMonth,
    month: parsedMonth,
    dayOfWeek: parsedDayOfWeek,
    astro,
  };
}

function datetimeAstro(a) {

  const degToRad = deg => (deg * Math.PI) / 180;
  const radToDeg = rad => (rad * 180) / Math.PI;

  const tpl = 0.0041666667;
  // In the original code, daycount(...) + 36526 is part of the NOAA offset
  const days = dayCount(a.yy, a.mm, a.dd) + 36526;

  // Note: a.tz was hours. We do (tzMinutes / 60 / 24) => (tzHours / 24)
  a.jd = days + 2415018.5 + tpl - a.tz / (60 * 24);
  a.jc = (a.jd - 2451545.0) / 36525.0;

  a.gmls = (280.46646 + a.jc * (36000.76983 + a.jc * 0.0003032)) % 360;
  a.gmas = 357.52911 + a.jc * (35999.05029 - 0.0001537 * a.jc);
  a.ecceo = 0.016708634 - a.jc * (0.000042037 + 0.0000001267 * a.jc);

  a.seqcrt =
    Math.sin(degToRad(a.gmas)) *
      (1.914602 - a.jc * (0.004817 + 0.000014 * a.jc)) +
    Math.sin(degToRad(2 * a.gmas)) * (0.019993 - 0.000101 * a.jc) +
    Math.sin(degToRad(3 * a.gmas)) * 0.000289;

  a.stl = a.gmls + a.seqcrt;
  a.sta = a.gmas + a.seqcrt;
  a.sal =
    a.stl - 0.00569 - 0.00478 * Math.sin(degToRad(125.04 - 1934.136 * a.jc));

  a.moe =
    23 +
    (26 +
      (21.448 - a.jc * (46.815 + a.jc * (0.00059 - a.jc * 0.001813))) / 60) /
      60;
  a.oc = a.moe + 0.00256 * Math.cos(degToRad(125.04 - 1934.136 * a.jc));

  a.rtasc = radToDeg(
    Math.atan2(
      Math.cos(degToRad(a.oc)) * Math.sin(degToRad(a.sal)),
      Math.cos(degToRad(a.sal))
    )
  );

  a.sdec = radToDeg(
    Math.asin(Math.sin(degToRad(a.oc)) * Math.sin(degToRad(a.sal)))
  );
  a.y = Math.tan(degToRad(a.oc / 2)) ** 2;

  a.eqtm =
    4 *
    radToDeg(
      a.y * Math.sin(degToRad(2 * a.gmls)) -
        2 * a.ecceo * Math.sin(degToRad(a.gmas)) +
        4 *
          a.ecceo *
          a.y *
          Math.sin(degToRad(a.gmas)) *
          Math.cos(degToRad(2 * a.gmls)) -
        0.5 * a.y * a.y * Math.sin(degToRad(4 * a.gmls)) -
        1.25 * a.ecceo * a.ecceo * Math.sin(degToRad(2 * a.gmas))
    );

  // NOAA formula wants the timezone offset in minutes
  // (720 - 4 * longitude - EoT + tzMinutes) / 1440
  a.hasr = radToDeg(
    Math.acos(
      Math.cos(degToRad(90.833)) /
        (Math.cos(degToRad(a.lat)) * Math.cos(degToRad(a.sdec))) -
        Math.tan(degToRad(a.lat)) * Math.tan(degToRad(a.sdec))
    )
  );

  a.solnoon = (720 - 4 * a.lng - a.eqtm + a.tz) / 1440;
  a.sunrise = (a.solnoon - (a.hasr * 4) / 1440) * 86400;
  a.sunset = (a.solnoon + (a.hasr * 4) / 1440) * 86400;
  a.solnoon = a.solnoon * 86400;

  return {
    sunrise: a.sunrise,
    sunset: a.sunset,
  };
}

// Basic day-of-year, ignoring leaps for brevity or do actual leaps:
function dayOfYear(year, month, day) {
  const d = new Date(Date.UTC(year, month - 1, day));
  const start = new Date(Date.UTC(year, 0, 1));
  const diff = d - start;
  return Math.floor(diff / 86400000) + 1;
}

function dayOfWeekMatches(date, cronObj) {
  if (cronObj.dayOfWeek === null) return true; // wildcard
  const dw = date.getUTCDay(); // Sunday=0..Saturday=6
  if (Array.isArray(cronObj.dayOfWeek)) {
    return cronObj.dayOfWeek.includes(dw);
  } else {
    return dw === cronObj.dayOfWeek;
  }
}

function dayCount(year, month, day) {
  const start = new Date(Date.UTC(year, 0, 1));
  const current = new Date(Date.UTC(year, month - 1, day));
  return Math.floor((current - start) / (1000 * 60 * 60 * 24));
}

function getNextEvent(cronExpr) {
    const tz = tzOffsets[APP_TIMEZONE];
    const lat = APP_LATITUDE;
    const lng = APP_LONGITUDE;
    const cronObj = parseCron(cronExpr);

    // Start searching from "now + 1 minute"
    let start = new Date();
    start.setUTCSeconds(0, 0);
    start.setUTCMinutes(start.getUTCMinutes() + 1 + tz);

    const startYear = start.getUTCFullYear();
    const maxYear = startYear + 2; // Avoid infinite loops: 2 years range

    for (let year = startYear; year <= maxYear; year++) {
        // which months
        const monthsToCheck =
        cronObj.month === null ? range(1, 12) : [cronObj.month];

        for (const m of monthsToCheck) {
        // skip months prior to "start" if same year
        if (year === startYear && m < start.getUTCMonth() + 1) {
            continue;
        }

        const daysInM = daysInMonth(m, year);
        const daysToCheck =
            cronObj.dayOfMonth === null ? range(1, daysInM) : [cronObj.dayOfMonth];

        for (const d of daysToCheck) {
            if (d > daysInM) continue; // invalid date

            // If we're in the start year & month, skip days < startDay
            if (
            year === startYear &&
            m === start.getUTCMonth() + 1 &&
            d < start.getUTCDate()
            ) {
            continue;
            }

            // Check day-of-week
            const testDay = new Date(Date.UTC(year, m - 1, d));
            if (!dayOfWeekMatches(testDay, cronObj)) {
            continue;
            }

            // If astro = '*', do the normal hour/minute iteration
            if (cronObj.astro === '*') {
            // Hours:
            const hoursToCheck =
                cronObj.hour === null ? range(0, 23) : [cronObj.hour];

            for (const h of hoursToCheck) {
                // skip hour < start hour if same date
                if (
                year === startYear &&
                m === start.getUTCMonth() + 1 &&
                d === start.getUTCDate() &&
                h < start.getUTCHours()
                ) {
                continue;
                }

                // Minutes
                const minsToCheck =
                cronObj.minute === null ? range(0, 59) : [cronObj.minute];

                for (const mm of minsToCheck) {
                if (
                    year === startYear &&
                    m === start.getUTCMonth() + 1 &&
                    d === start.getUTCDate() &&
                    h === start.getUTCHours() &&
                    mm < start.getUTCMinutes()
                ) {
                    continue;
                }

                const candidate = new Date(Date.UTC(year, m - 1, d, h, mm, 0, 0));
                if (candidate >= start) {
                    // Return the first match
                    return candidate;
                }
                }
            }
            } else {
            // astro = 'r', 's', or 'rs'
            // we compute sunrise & sunset for this day
            const { sunrise, sunset } = datetimeAstro({
                dd: d,
                mm: m,
                yy: year - 2000,
                tz,
                lat,
                lng,
            });

            // sunrise/sunset are seconds from midnight local
            // build candidate Date objects
            const dateMidnight = new Date(Date.UTC(year, m - 1, d, 0, 0, 0, 0));

            // We'll collect possible astro times in an array
            const astroTimes = [];

            if (cronObj.astro.includes('r')) {
                const sr = new Date(dateMidnight.getTime() + sunrise * 1000);
                astroTimes.push(sr);
            }
            if (cronObj.astro.includes('s')) {
                const ss = new Date(dateMidnight.getTime() + sunset * 1000);
                astroTimes.push(ss);
            }

            // sort ascending
            astroTimes.sort((a, b) => a - b);

            // find first astroTime >= start
            for (const dt of astroTimes) {
                if (dt >= start) {
                return dt;
                }
            }
            // if none matched, continue to next day
            }
        }
        }
    }

    console.log(\`No matching time found within 2 years after \${start}\`);
    return null;
}

// Helper: integer range
function range(start, end) {
    const arr = [];
    for (let i = start; i <= end; i++) {
        arr.push(i);
    }
    return arr;
}

// Days in month
function daysInMonth(m, y) {
    // new Date(y, m, 0) => last day of previous month
    return new Date(Date.UTC(y, m, 0)).getUTCDate();
}
`,
    });

    for (let i = 0; i < SCHEDULES.length; i += 1) {
        blocks = blocks.concat(SCHEDULES[i].getCodeBlocks());
    }


    return blocks;
};
